import { useCallback } from 'react';
import 'typeface-rubik';
import '@fontsource/jetbrains-mono';
import {
    AppBar,
    CircularProgressCenter,
    CMSView,
    CMSViewsBuilder,
    DataSourceDelegate,
    Drawer,
    EntityCollection,
    FireCMS,
    ModeControllerProvider,
    NavigationRoutes,
    Scaffold,
    SideDialogs,
    SnackbarProvider,
    useBuildLocalConfigurationPersistence,
    useBuildModeController,
    useBuildNavigationController,
    useValidateAuthenticator,
} from '@firecms/core';
import {
    FirebaseAuthController,
    FirebaseLoginView,
    FirebaseSignInProvider,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
    useInitialiseFirebase,
} from '@firecms/firebase';
import { useBuildUserManagement, useUserManagementPlugin, userManagementAdminViews } from '@firecms/user_management';
import { useEntityHistoryPlugin } from '@firecms/entity_history';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IS_DEVELOPMENT } from './common';
import { createAuthUser, sendPasswordResetEmail } from './common/auth';
import logo from './assets/favicon.svg';
import { clientsCollection } from './collections/clients/collection.clients';
import { configsCollection } from './collections/configs/collection.configs';
import { firebaseConfig } from './firebase_config';

const queryClient = new QueryClient();

export function App() {
    if (!firebaseConfig?.projectId) {
        throw new Error(
            'Firebase config not found. Check your .env file and ensure VITE_FIREBASE_PROJECT_ID is set.',
        );
    }

    const { firebaseApp, firebaseConfigLoading, configError } = useInitialiseFirebase({ firebaseConfig });

    if (firebaseConfigLoading || !firebaseApp) {
        return <CircularProgressCenter />;
    }

    if (configError) {
        return <>{configError}</>;
    }

    return <CMSApp firebaseApp={firebaseApp} />;
}

function CMSApp({ firebaseApp }: { firebaseApp: import('firebase/app').FirebaseApp }) {
    const modeController = useBuildModeController();
    const storageSource = useFirebaseStorageSource({ firebaseApp });

    const dataSourceDelegate = useFirestoreDelegate({ firebaseApp, localTextSearchEnabled: true }) as unknown as DataSourceDelegate;

    const signInOptions: FirebaseSignInProvider[] = ['password'];
    const authController: FirebaseAuthController = useFirebaseAuthController({ firebaseApp, signInOptions });

    const userManagement = useBuildUserManagement({
        dataSourceDelegate,
        authController,
    });

    const userManagementWithAuthCreate = {
        ...userManagement,
        saveUser: async (user: any) => {
            if (!user.uid && user.email) {
                const uid = await createAuthUser(user.email);
                await sendPasswordResetEmail(user.email);
                return userManagement.saveUser({ ...user, uid });
            }
            return userManagement.saveUser(user);
        },
    };

    const { authLoading, canAccessMainView, notAllowedError } = useValidateAuthenticator({
        authController,
        disabled: userManagementWithAuthCreate.loading,
        authenticator: userManagementWithAuthCreate.authenticator,
        dataSourceDelegate,
        storageSource,
    });

    const userManagementPlugin = useUserManagementPlugin({ userManagement: userManagementWithAuthCreate });
    const entityHistoryPlugin = useEntityHistoryPlugin({ getUser: userManagement.getUser });

    const collectionsBuilder = useCallback(
        async (): Promise<EntityCollection[]> => {
            return [
                {
                    ...clientsCollection({ dataSourceDelegate }),
                    permissions: { read: true, create: true, edit: true, delete: true },
                },
                {
                    ...configsCollection({ dataSourceDelegate, firebaseApp }),
                    permissions: { read: true, create: true, edit: true, delete: true },
                },
            ];
        },
        [dataSourceDelegate, firebaseApp],
    );

    const viewsBuilder: CMSViewsBuilder = useCallback((): CMSView[] => [], []);

    const navigationController = useBuildNavigationController({
        collections: collectionsBuilder,
        views: viewsBuilder,
        adminViews: userManagementAdminViews,
        authController,
        collectionPermissions: userManagementWithAuthCreate.collectionPermissions,
        plugins: [userManagementPlugin, entityHistoryPlugin],
        dataSourceDelegate,
    });

    const userConfigPersistence = useBuildLocalConfigurationPersistence();

    return (
        <QueryClientProvider client={queryClient}>
            <SnackbarProvider>
                <ModeControllerProvider value={modeController}>
                    <FireCMS
                        navigationController={navigationController}
                        authController={authController}
                        userConfigPersistence={userConfigPersistence}
                        dataSourceDelegate={dataSourceDelegate}
                        storageSource={storageSource}
                    >
                        {({ loading }) => {
                            if (loading || authLoading) return <CircularProgressCenter size={'large'} />;

                            if (!canAccessMainView) {
                                return (
                                    <FirebaseLoginView
                                        allowSkipLogin={false}
                                        disableSignUpScreen={true}
                                        signInOptions={signInOptions}
                                        firebaseApp={firebaseApp}
                                        authController={authController}
                                        notAllowedError={notAllowedError}
                                    />
                                );
                            }

                            return (
                                <Scaffold logo={logo} autoOpenDrawer={false}>
                                    {IS_DEVELOPMENT && <DevBanner />}
                                    <AppBar title="Vessel Mapper CMS" />
                                    <Drawer />
                                    <NavigationRoutes />
                                    <SideDialogs />
                                </Scaffold>
                            );
                        }}
                    </FireCMS>
                </ModeControllerProvider>
            </SnackbarProvider>
        </QueryClientProvider>
    );
}

function DevBanner() {
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%',
            backgroundColor: 'red', color: 'white', textAlign: 'center',
            fontWeight: 'bold', fontSize: '10px', zIndex: 9999, height: 15,
        }}>
            DEVELOPMENT
        </div>
    );
}
